import FormItemTextArea from "./FormItemTextArea";
import FormItemRichText from "./FormItemRichText";
import FormItemText from "./FormItemText";
import FormItemToggle from "./FormItemToggle";
import React, {Fragment} from "react";

import {UseFormRegister, FieldValues, FieldErrors, Control} from "react-hook-form";

import PanelWrapper from "./PanelWrapper";
import FormItemDatePicker from "./FormItemDatePicker";
import parse from "date-fns/parse";
import format from "date-fns/format";
import PanelWrapperNoHeader from "./PanelWrapperNoHeader";
import AgreementSection from "root/Wieldy/ApplicationModules/Child/Body/Agreements/AgreementSection";
import PanelWrapperNull from "root/Wieldy/ApplicationModules/Child/Body/Agreements/FormComponents/PanelWrapperNull";
import FormItemMediaExchange
    from "root/Wieldy/ApplicationModules/Child/Body/Agreements/FormComponents/FormItemMediaExchange";

/**
 * Enum for form item types ( E.g. 'input' or 'textarea' ). These define how the item will be rendered
 *
 * @readonly
 * @enum {FormSectionTypes}
 */
export const FormSectionTypes = {
    INPUT: 'input',
    TEXTAREA: 'textarea',
    RICHTEXT: 'richtext',
    TOGGLE: 'toggle',
    CHECKBOX: 'checkbox',
    MEDIA_EXCHANGE: 'media',
    DATE: 'date',
}

/**
 * Enum for form item types types ( E.g. 'int' or 'string' ). These define how the item will be stored in the database
 *
 * @readonly
 * @enum {FormDataTypes}
 */
export const FormDataTypes = {
    INT: 'int',
    STRING: 'string',
    DATE: 'date',
    BOOL: 'bool',
}

const getPrefixForItemType = (itemType) =>
{
    switch (itemType)
    {
        case FormSectionTypes.INPUT:
            return "01";
        case FormSectionTypes.TEXTAREA:
            return "02";
        case FormSectionTypes.TOGGLE:
            return "03";
        case FormSectionTypes.RICHTEXT:
            return "04";
        case FormSectionTypes.CHECKBOX:
            return "05";
        case FormSectionTypes.DATE:
            return "06";
        case FormSectionTypes.MEDIA_EXCHANGE:
            return "06";
        default:
            return "99";
    }
}

const getPrefixForDataType = (itemType) =>
{
    switch (itemType)
    {
        case FormDataTypes.INT:
            return "01";
        case FormDataTypes.STRING:
            return "02";
        case FormDataTypes.DATE:
            return "03";
        case FormDataTypes.BOOL:
            return "04";
        default:
            return "99";
    }
}

const getPrefix = ( item) =>
{
    switch ( item.field_type )
    {
        case FormSectionTypes.TOGGLE:
        case FormSectionTypes.CHECKBOX:
        case FormSectionTypes.MEDIA_EXCHANGE:
            return getPrefixForItemType(item.field_type) + getPrefixForDataType(FormDataTypes.BOOL);
        default:
            return getPrefixForItemType(item.field_type) + getPrefixForDataType(item.data_type);
    }
}

/**
 * Translates type item type into a prefix so the server side can determine which data field to update
 * when saving dynamic form data
 *
 * @param {array} item - The data type of the item being processed
 */
export const getPrefixForType = (item) =>
{
    return getPrefix(item);
}

/**
 * Generates an unique data field name for use by react-hook-form. This will also be used by the back-end to
 * extract the data fields and determine the ID of the form data item to update with the new data
 *
 * @param {array} item - The form data record as defined in the database
 * @param {FormSectionTypes} itemType - The data type of the item being processed
 *
 * @returns {string} returns an unique number comprised of the item type, ID of the data item and the name of the data item
 */
const itemName = (item) =>
{
    return getPrefixForType(item) + '#' + 'form' + '#' + item.cpsp_id + '#' + item.cpr_id;
}

/**
 * Generates a higher-level media item name for form items with sub-attributes
 *
 * @param {array} item - The form data record as defined in the database
 * @param {FormSectionTypes} itemType - The data type of the item being processed
 *
 * @returns {string} returns an unique number comprised of the item type, ID of the data item and the name of the data item
 */
const mediaItemName = (item) =>
{
    return 'media#' + item.cpsp_id ;
}

/**
 * Parses a dd/mm/yyyy date string into a date object to be passed into the datepicker control
 *
 * @param {string} dateStr - The form data record as defined in the database
 *
 * @returns {Date} returns a JavaScript date object
 */
const parseDate = (dateStr) =>
{
    if (dateStr === null || dateStr === undefined)
    {
        return null;
    }

    const reg = /^(((0[1-9]|[12]\d|3[01])\/(0[13578]|1[02])\/((19|[2-9]\d)\d{2}))|((0[1-9]|[12]\d|30)\/(0[13456789]|1[012])\/((19|[2-9]\d)\d{2}))|((0[1-9]|1\d|2[0-8])\/02\/((19|[2-9]\d)\d{2}))|(29\/02\/((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|(([1][26]|[2468][048]|[3579][26])00))))$/g;

    const result = reg.test(dateStr);

    const date = result ? parse(dateStr, 'dd/MM/yyyy', new Date()) : null;

    return date;
}

const responseFieldNames = {
    [FormSectionTypes.INPUT]: 'response_text',
    [FormSectionTypes.TEXTAREA]: 'response_text',
    [FormSectionTypes.RICHTEXT]: 'response_text',
    [FormSectionTypes.TOGGLE]: 'response_bool',
    [FormSectionTypes.CHECKBOX]: 'response_bool',
    [FormSectionTypes.DATE]: 'response_date',
    [FormSectionTypes.MEDIA_EXCHANGE]: 'response_bool',
}

/**
 * Generates an array of react-hook-form data elements to pass into useForm from the list
 * of data elements described in the form_item database table
 *
 * @param {array} sections - The form data record as defined in the databse
 */
export const mapDataFieldsForFormFields = (questions) =>
{
    const formFields = questions.reduce((cq, rq) =>
    {
        const sectionType = rq.field_type;
        const response_field_name = responseFieldNames[sectionType];

        switch (sectionType)
        {
            case FormSectionTypes.INPUT:
            case FormSectionTypes.TEXTAREA:
            case FormSectionTypes.RICHTEXT:
                cq[itemName(rq)] = rq[response_field_name];
                break;
            case FormSectionTypes.TOGGLE:
            case FormSectionTypes.CHECKBOX:
                cq[itemName(rq)] = +rq[response_field_name] === 1;
                break;
            case FormSectionTypes.DATE:
                cq[itemName(rq)] = parseDate(rq[response_field_name]);
                break;
            case FormSectionTypes.MEDIA_EXCHANGE:

                if ( cq.media === undefined )
                {
                    cq.media = {} ;
                }

                cq.media[ itemName(rq) + '#letterbox_item_type_id'] = rq['letterbox_item_type_id'] == null ? null : +rq['letterbox_item_type_id'] ;
                cq.media[ itemName(rq) + '#periodicity_id'] = rq['periodicity_id'] == null ? null : { value: +rq['periodicity_id'] } ;
                cq.media[ itemName(rq) + '#max_allowed_count'] = rq['max_allowed_count'] ;
                cq.media[ itemName(rq) + '#toggle'] = +rq[response_field_name] === 1;

                break;
        }

        return cq;
    }, {});

    return formFields;
}

/**
 * Generates an array of react-hook-form data elements to pass into useForm from the list
 * of data elements described in the form_item database table
 *
 * @param {array} sections - The form data record as defined in the databse
 */
export const getWatchFields = (formName, questions) =>
{
    const fieldsWithDependencies = questions.filter ((item) => {
        return item.metadata !== null ;
    });

    // First we scan all the records with dependencies and distil down the list of unique
    // field names that will be watched
    const targetFieldNames = fieldsWithDependencies.reduce((accumulator, current) => {

        const js = current.metadata ;

        const obj = JSON.parse ( js ) ;

        if ( obj.showWhenFieldSet !== undefined )
        {
            if ( !accumulator.includes(obj.showWhenFieldSet))
            {
                // Push the field name of the field that will be watched
                accumulator.push ( obj.showWhenFieldSet ) ;
            }
        }

        return accumulator ;
    }, [])

    // Now we scan the list of questions and extract the records that match the field names
    const targetItems = questions.filter((item) => {
        if ( targetFieldNames.includes ( item.data_item_name ) )
        {
            return true ;
        }
    }, [])

    // Now form an ordered array of field names and hook form names so we can apply the offset to the
    // record when rendering so we can hide the field if the dependency is not met
    //
    // This depends on the fact that the array of items returned by watch() will be an array of true/false
    // values that correspond to the array of field names returned by this function
    //
    const fieldsToWatch = targetItems.map((item) => {
            return { dataItemName: item.data_item_name, hookFormName: formName + '.' + itemName(item) };
    }, [])

    // Now, finally, we loop through all the questions again, and apply a watch Offset field which will be used later

    for ( let i = 0 ; i < questions.length ; i++ )
    {
        const item = questions[i] ;

        const js = item.metadata ;

        if ( js !== null)
        {
            const obj = JSON.parse ( js ) ;

            if ( obj.showWhenFieldSet !== undefined )
            {
                const fieldName = obj.showWhenFieldSet ;

                const watchOffset = fieldsToWatch.findIndex((element) => {
                    return element.dataItemName === fieldName ;
                })

                if ( watchOffset !== -1 )
                {
                    item.watchOffset = watchOffset ;
                }
            }
        }
    }

    const finalFieldsToWatch = fieldsToWatch.map((item) => {
        return item.hookFormName ;
    });

    return finalFieldsToWatch;
}

const groupIDName = 'plan_section_id' ;
const groupTitleName = 'plan_section_title' ;

/**
 * Generates an array of react-hook-form data elements to pass into useForm from the list
 * of data elements described in the form_item database table
 *
 * @param {array} questions - The form data record as defined in the databse
 */
export const mapDataFieldsIntoSections = (questions, formName) =>
{
    const hasGroups = questions.reduce((cur, sectionRec) =>
    {
        return sectionRec[groupIDName] !== null ? true : cur;
    }, false);

    if (hasGroups)
    {
        const formFields = questions.reduce((cur, sectionRec) =>
        {
            if (cur[+(sectionRec[groupIDName])] === undefined)
            {
                cur[+(sectionRec[groupIDName])] = {sectionTitle: sectionRec[groupTitleName], sectionName: formName, sectionType: +sectionRec.section_type, sectionOrder: +sectionRec.section_order, questions: [sectionRec]};
            }
            else
            {
                cur[+(sectionRec[groupIDName])].questions.push(sectionRec);
            }

            return cur;
        }, []);

        const sorted = formFields.sort((a,b) =>
        {
            if(a.sectionOrder > b.sectionOrder)
            {
                return 1;
            }
            else if ( a.sectionOrder < b.sectionOrder )
            {
                return -1;
            }
            else return 0;
        });

        return sorted;
    }
    else
    {
        return [{sectionTitle: 'All', sectionName: formName, questions: questions}];
    }
}

export const preProcessFormData = ( formData ) =>
{
    const formDataCopy = {...formData};

    Object.keys(formDataCopy).forEach((key) =>
    {
        if (formDataCopy[key] instanceof Date)
        {
            formDataCopy[key] = format(formDataCopy[key], 'dd/MM/yyyy') ;
        }
    })

    return formDataCopy;
}

/**
 * Renders all of the form data items based on the form types. All rendered hook-form components will be rendered and connected to the form using
 * the unique item names generated by itemName() above
 *
 * @param {array} renderItems - The individual form item records returned from the database
 * @param {Control<FieldValues, any>} control - react-hook-form control returned from useForm
 * @param {UseFormRegister<FieldValues>} register - react-hook-form register returned from useForm
 * @param {FieldErrors<FieldValues>} formErrors - react-hook-form error list returned from useForm
 */export const renderSection = ({sectionName, renderItems, control, register, formErrors, panelWrapper, watchFieldArray}) =>
{
    const fieldsToRender = renderItems.filter((item) => {
        if ( item.watchOffset !== undefined )
        {
            const watchIndex = item.watchOffset ;

            const watchValue = watchFieldArray[watchIndex] ;

            return watchValue === true ;
        }
        else
        {
            return true ;
        }
    }) ;

    const titleFieldName = 'heading' ;

    const fields = fieldsToRender.map((item, index) =>
    {
        const sectionType = item.field_type;

        const WrapperType = panelWrapper ? panelWrapper : PanelWrapperNull;

        switch (sectionType)
        {
            case FormSectionTypes.TEXTAREA:
                return <FormItemTextArea heading={item[titleFieldName]} name={itemName(item, sectionType)} formSectionName={sectionName} register={register} formErrors={formErrors} key={'cnt' + index}/>
            case FormSectionTypes.RICHTEXT:
                return (
                    <WrapperType help_text={item.help_text} title={item[titleFieldName]} key={'cnt' + index}>
                        <FormItemRichText heading={null} name={itemName(item, sectionType)} formSectionName={sectionName} control={control} register={register}
                                          formErrors={formErrors}/>
                    </WrapperType>
                )

            case FormSectionTypes.INPUT:
                return (
                    <WrapperType help_text={item.help_text} title={item[titleFieldName]} key={'cnt' + index}>
                        <FormItemText heading={item[titleFieldName]} name={itemName(item, sectionType)} formSectionName={sectionName} register={register} formErrors={formErrors}/>
                    </WrapperType>
                )

            case FormSectionTypes.MEDIA_EXCHANGE:
                return (
                    <WrapperType help_text={item.help_text} title={item[titleFieldName]} key={'cnt' + index}>
                        <FormItemMediaExchange heading={item[titleFieldName]} item={item} objectName={mediaItemName(item)} name={itemName(item, sectionType)} formSectionName={sectionName} control={control} register={register} formErrors={formErrors}/>
                    </WrapperType>
                )

            case FormSectionTypes.DATE:
                return (
                    <WrapperType help_text={item.help_text} title={item[titleFieldName]} key={'cnt' + index}>
                        <FormItemDatePicker heading={null} name={itemName(item, sectionType)} formSectionName={sectionName} control={control} register={register}
                                            formErrors={formErrors}/>
                    </WrapperType>
                )

            case FormSectionTypes.TOGGLE:
            case FormSectionTypes.CHECKBOX:``

                return (
                    <PanelWrapperNoHeader help_text={item.help_text} title={item[titleFieldName]} key={'cnt' + index}>
                        <FormItemToggle heading={item[titleFieldName]} name={itemName(item, sectionType)} formSectionName={sectionName} register={register} control={control}
                                        formErrors={formErrors}/>
                    </PanelWrapperNoHeader>
                )
            default:
                return <div key={'cnt' + index}>Cannot render item</div>
        }
    });

    return (
        <div className='row' key={'abrow' + sectionName}>
            <div className='col-md-12' key='abcol'>
                {fields}
            </div>
        </div>
    )
}

export const renderSections = (sections, isGrouped, control, register, formErrors, watchFieldArray ) =>
{
    if (isGrouped)
    {
        return sections.map((section, index) =>
        {
            const params = {
                title: section.sectionTitle,
                sectionName: section.sectionName,
                renderItems: section.questions,
                control: control,
                register: register,
                formErrors: formErrors,
                panelWrapper: PanelWrapperNull,
                watchFieldArray: watchFieldArray
            }

            return (
                <AgreementSection title={section.sectionTitle} key={'stitle' + index }>
                    <div className='row'>
                        <div className='col-md-12'>
                            {renderSection(params)}
                        </div>
                    </div>
                </AgreementSection>
            )
        })

    }
    else
    {
        return sections.map((section) =>
        {
            const params = {
                title: section.sectionTitle,
                sectionName: section.sectionName,
                renderItems: section.questions,
                control: control,
                register: register,
                formErrors: formErrors,
                panelWrapper: PanelWrapper,
                watchFieldArray: watchFieldArray
            }

            return renderSection(params);
        })
    }
}